import { isArray, isNumber } from "@thi.ng/checks";
import { defmulti } from "@thi.ng/defmulti";
import { DEFAULT_SAMPLES, Type } from "@thi.ng/geom-api";
import { sample as _arcVertices } from "@thi.ng/geom-arc";
import { resample } from "@thi.ng/geom-resample";
import { sampleCubic, sampleQuadratic } from "@thi.ng/geom-splines";
import { cossin, TAU } from "@thi.ng/math";
import { add2, add3, cartesian2, madd2, set2 } from "@thi.ng/vectors";
import { Polygon } from "../api/polygon";
import { dispatch } from "../internal/dispatch";
export const vertices = defmulti(dispatch);
vertices.addAll({
    // e +----+ h
    //   |\   :\
    //   |f+----+ g
    //   | |  : |
    // a +-|--+d|
    //    \|   \|
    //   b +----+ c
    //
    [Type.AABB]: ({ pos, size }) => {
        const [px, py, pz] = pos;
        const [qx, qy, qz] = add3([], pos, size);
        return [
            [px, py, pz],
            [px, py, qz],
            [qx, py, qz],
            [qx, py, pz],
            [px, qy, pz],
            [px, qy, qz],
            [qx, qy, qz],
            [qx, qy, pz],
        ];
    },
    [Type.ARC]: ($, opts) => _arcVertices($.pos, $.r, $.axis, $.start, $.end, opts),
    [Type.CIRCLE]: ($, opts = DEFAULT_SAMPLES) => {
        const pos = $.pos;
        const r = $.r;
        let [num, last] = circleOpts(opts, r);
        const delta = TAU / num;
        last && num++;
        const buf = new Array(num);
        for (let i = 0; i < num; i++) {
            buf[i] = cartesian2(null, [r, i * delta], pos);
        }
        return buf;
    },
    [Type.CUBIC]: ($, opts) => sampleCubic($.points, opts),
    [Type.ELLIPSE]: ($, opts = DEFAULT_SAMPLES) => {
        const buf = [];
        const pos = $.pos;
        const r = $.r;
        let [num, last] = circleOpts(opts, Math.max($.r[0], $.r[1]));
        const delta = TAU / num;
        last && num++;
        for (let i = 0; i < num; i++) {
            buf[i] = madd2([], cossin(i * delta), r, pos);
        }
        return buf;
    },
    [Type.GROUP]: ({ children }) => children.reduce((acc, $) => acc.concat(vertices($)), []),
    [Type.PATH]: ($, opts) => {
        const _opts = isNumber(opts) ? { num: opts } : opts;
        let verts = [];
        for (let segs = $.segments, n = segs.length - 1, i = 0; i <= n; i++) {
            const s = segs[i];
            if (s.geo) {
                verts = verts.concat(vertices(s.geo, Object.assign(Object.assign({}, _opts), { last: i === n && !$.closed })));
            }
        }
        return verts;
    },
    [Type.POINTS]: ($) => $.points,
    [Type.POLYGON]: ($, opts) => resample($.points, opts, true),
    [Type.POLYLINE]: ($, opts) => resample($.points, opts),
    [Type.QUADRATIC]: ($, opts) => sampleQuadratic($.points, opts),
    [Type.RECT]: ($, opts) => {
        const p = $.pos;
        const q = add2([], p, $.size);
        const verts = [set2([], p), [q[0], p[1]], q, [p[0], q[1]]];
        return opts != null ? vertices(new Polygon(verts), opts) : verts;
    },
});
vertices.isa(Type.LINE, Type.POLYLINE);
vertices.isa(Type.POINTS3, Type.POINTS);
vertices.isa(Type.QUAD, Type.POLYGON);
vertices.isa(Type.TRIANGLE, Type.POLYGON);
/**
 * Takes array of vectors or an `IShape`. If the latter, calls {@link vertices}
 * and return result, else returns original array.
 *
 * @param shape
 */
export const ensureVertices = (shape) => isArray(shape) ? shape : vertices(shape);
const circleOpts = (opts, r) => isNumber(opts)
    ? [opts, false]
    : [
        opts.theta
            ? Math.floor(TAU / opts.theta)
            : opts.dist
                ? Math.floor(TAU / (opts.dist / r))
                : opts.num || DEFAULT_SAMPLES,
        opts.last === true,
    ];
